import apiClient from "./apiClient";

const API_BASE_URL = process.env.REACT_APP_BASE_URL + "/api";

class ApiService {
  // Tenant APIs
  async getAllTenants() {
    const response = await apiClient.get("/admin/tenants");
    return response.data;
  }

  // Department APIs
  async getDepartments() {
    const response = await apiClient.get("/admin/departments");
    return response.data;
  }

  async createDepartment(data: any) {
    const response = await apiClient.post("/admin/departments", data);
    return response.data;
  }

  async updateDepartment(id: number, data: any) {
    const response = await apiClient.put(`/admin/departments/${id}`, data);
    return response.data;
  }

  async deleteDepartment(id: number) {
    const response = await apiClient.delete(`/admin/departments/${id}`);
    return response.data;
  }

  // Designation APIs
  async getDesignations() {
    const response = await apiClient.get("/admin/designations");
    return response.data;
  }

  async getDesignationsByDepartment(departmentId: number) {
    const response = await apiClient.get(
      `/admin/designations/department/${departmentId}`
    );
    return response.data;
  }

  async createDesignation(data: any) {
    const response = await apiClient.post("/admin/designations", data);
    return response.data;
  }

  async updateDesignation(id: number, data: any) {
    const response = await apiClient.put(`/admin/designations/${id}`, data);
    return response.data;
  }

  async deleteDesignation(id: number) {
    const response = await apiClient.delete(`/admin/designations/${id}`);
    return response.data;
  }

  // Worker Type APIs
  async getWorkerTypes() {
    const response = await apiClient.get("/admin/worker-types");
    return response.data;
  }

  async createWorkerType(data: any) {
    const response = await apiClient.post("/admin/worker-types", data);
    return response.data;
  }

  async updateWorkerType(id: number, data: any) {
    const response = await apiClient.put(`/admin/worker-types/${id}`, data);
    return response.data;
  }

  async deleteWorkerType(id: number) {
    const response = await apiClient.delete(`/admin/worker-types/${id}`);
    return response.data;
  }

  // Shift APIs
  async getShifts() {
    const response = await apiClient.get("/admin/shifts");
    return response.data;
  }

  async createShift(data: any) {
    const response = await apiClient.post("/admin/shifts", data);
    return response.data;
  }

  async updateShift(id: number, data: any) {
    const response = await apiClient.put(`/admin/shifts/${id}`, data);
    return response.data;
  }

  async deleteShift(id: number) {
    const response = await apiClient.delete(`/admin/shifts/${id}`);
    return response.data;
  }

  async getShiftById(id: number) {
    const response = await apiClient.get(`/admin/shifts/${id}`);
    return response.data;
  }

  async searchShifts(name: string) {
    const response = await apiClient.get(
      `/admin/shifts/search?name=${encodeURIComponent(name)}`
    );
    return response.data;
  }

  async getShiftsByWorkerType(workerTypeId: number) {
    const response = await apiClient.get(
      `/admin/shifts/worker-type/${workerTypeId}`
    );
    return response.data;
  }

  // Leave Master APIs
  async getLeaveMasters() {
    const response = await apiClient.get("/admin/leave-master");
    return response.data;
  }

  async createLeaveMaster(data: any) {
    const response = await apiClient.post("/admin/leave-master", data);
    return response.data;
  }

  async updateLeaveMaster(id: number, data: any) {
    const response = await apiClient.put(`/admin/leave-master/${id}`, data);
    return response.data;
  }

  async deleteLeaveMaster(id: number) {
    const response = await apiClient.delete(`/admin/leave-master/${id}`);
    return response.data;
  }

  async searchLeaveMasters(leaveType: string) {
    const response = await apiClient.get(
      `/admin/leave-master/search?leave_type=${encodeURIComponent(leaveType)}`
    );
    return response.data;
  }

  async getLeaveMastersByFrequency(frequency: string) {
    const response = await apiClient.get(
      `/admin/leave-master/by-frequency?frequency=${encodeURIComponent(
        frequency
      )}`
    );
    return response.data;
  }

  async getPaidLeaveMasters() {
    const response = await apiClient.get("/admin/leave-master/paid");
    return response.data;
  }

  async getApprovalRequiredLeaveMasters() {
    const response = await apiClient.get(
      "/admin/leave-master/approval-required"
    );
    return response.data;
  }

  // Employee Leave Allocation APIs
  async getEmployeeLeaveAllocations() {
    const response = await apiClient.get("/admin/employee-leave-allocation");
    return response.data;
  }

  async createEmployeeLeaveAllocation(data: any) {
    const response = await apiClient.post(
      "/admin/employee-leave-allocation",
      data
    );
    return response.data;
  }

  async updateEmployeeLeaveAllocation(id: number, data: any) {
    const response = await apiClient.put(
      `/admin/employee-leave-allocation/${id}`,
      data
    );
    return response.data;
  }

  async deleteEmployeeLeaveAllocation(id: number) {
    const response = await apiClient.delete(
      `/admin/employee-leave-allocation/${id}`
    );
    return response.data;
  }

  async getEmployeeLeaveAllocationById(id: number) {
    const response = await apiClient.get(
      `/admin/employee-leave-allocation/${id}`
    );
    return response.data;
  }

  async getEmployeeLeaveAllocationsByEmployee(employeeId: number) {
    const response = await apiClient.get(
      `/admin/employee-leave-allocation/employee/${employeeId}`
    );
    return response.data;
  }

  async getEmployeeLeaveAllocationsByEmployeeAndYear(
    employeeId: number,
    year: number
  ) {
    const response = await apiClient.get(
      `/admin/employee-leave-allocation/employee/${employeeId}/year/${year}`
    );
    return response.data;
  }

  async getEmployeeLeaveAllocationsByLeaveType(leaveId: number) {
    const response = await apiClient.get(
      `/admin/employee-leave-allocation/leave-type/${leaveId}`
    );
    return response.data;
  }

  async getEmployeeLeaveAllocationsByYear(year: number) {
    const response = await apiClient.get(
      `/admin/employee-leave-allocation/year/${year}`
    );
    return response.data;
  }

  async searchEmployeeLeaveAllocationsByEmployeeName(employeeName: string) {
    const response = await apiClient.get(
      `/admin/employee-leave-allocation/search/employee?name=${encodeURIComponent(
        employeeName
      )}`
    );
    return response.data;
  }

  // Employee Leave Application APIs
  async getLeaveApplications() {
    const response = await apiClient.get("/admin/employee-leave-application");
    return response.data;
  }

  async createLeaveApplication(data: any) {
    const response = await apiClient.post(
      "/admin/employee-leave-application",
      data
    );
    return response.data;
  }

  async updateLeaveApplication(id: number, data: any) {
    const response = await apiClient.put(
      `/admin/employee-leave-application/${id}`,
      data
    );
    return response.data;
  }

  async deleteLeaveApplication(id: number) {
    const response = await apiClient.delete(
      `/admin/employee-leave-application/${id}`
    );
    return response.data;
  }

  async getLeaveApplicationById(id: number) {
    const response = await apiClient.get(
      `/admin/employee-leave-application/${id}`
    );
    return response.data;
  }

  async getLeaveApplicationsByEmployee(employeeId: number) {
    const response = await apiClient.get(
      `/admin/employee-leave-application/employee/${employeeId}`
    );
    return response.data;
  }

  async approveLeaveApplication(id: number, data: any) {
    const response = await apiClient.post(
      `/admin/employee-leave-application/${id}/approve`,
      data
    );
    return response.data;
  }

  async rejectLeaveApplication(id: number, data: any) {
    const response = await apiClient.post(
      `/admin/employee-leave-application/${id}/reject`,
      data
    );
    return response.data;
  }

  async getPendingLeaveApplications() {
    const response = await apiClient.get(
      "/admin/employee-leave-application/pending/applications"
    );
    return response.data;
  }

  async getLeaveBalance(employeeId: number, leaveId: number) {
    const response = await apiClient.get(
      `/admin/employee-leave-application/balance/${employeeId}/${leaveId}`
    );
    return response.data;
  }

  async getManagersForEmployee(employeeId: number) {
    const response = await apiClient.get(
      `/admin/employee-leave-application/managers/${employeeId}`
    );
    return response.data;
  }

  async updateEmployeeLeaveAllocationUsedDays(id: number, usedDays: number) {
    const response = await apiClient.put(
      `/admin/employee-leave-allocation/${id}/used-days`,
      { usedDays }
    );
    return response.data;
  }

  async getCurrentYearEmployeeLeaveAllocations() {
    const response = await apiClient.get(
      "/admin/employee-leave-allocation/current-year"
    );
    return response.data;
  }

  // Employee APIs

  async uploadProfilePicture(employeeId: number, file: File) {
    const formData = new FormData();
    formData.append('document', file); // The backend uploadMiddleware expects the field name 'document'
    
    const response = await apiClient.post(`/admin/employee/${employeeId}/profile-picture`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getEmployee(id: number) {
    // Use the /:id route (canAccessEmployee guard) instead of /directory/:id
    // which requires explicit EMPLOYEE_READ permission. Employees can always
    // access their own record via the /:id route.
    const response = await apiClient.get(`/admin/employee/${id}`);
    return response.data;
  }

  async createEmployee(data: any) {
    const response = await apiClient.post("/admin/employee/create", data);
    return response.data;
  }

  async updateEmployee(id: number, data: any) {
    const response = await apiClient.put(`/admin/employee/${id}`, data);
    return response.data;
  }

  async deleteEmployee(id: number) {
    const response = await apiClient.delete(`/admin/employee/${id}`);
    return response.data;
  }

  async getFile(url: string) {
    let cleanPath = url;
    if (cleanPath.startsWith('/api/')) cleanPath = cleanPath.substring(5);
    if (cleanPath.startsWith('/')) cleanPath = cleanPath.substring(1);
    
    return apiClient.get(`/${cleanPath}`, { responseType: 'blob' });
  }

  // Employee Address APIs
  async getEmployeeAddresses() {
    const response = await apiClient.get("/admin/employee-address");
    return response.data;
  }

  async createEmployeeAddress(data: any) {
    const response = await apiClient.post(
      "/admin/employee-address/create",
      data
    );
    return response.data;
  }

  // Holiday Master APIs
  async getHolidayMasters() {
    const response = await apiClient.get("/admin/holiday-master");
    return response.data;
  }

  async createHolidayMaster(data: any) {
    const response = await apiClient.post("/admin/holiday-master", data);
    return response.data;
  }

  async updateHolidayMaster(id: number, data: any) {
    const response = await apiClient.put(`/admin/holiday-master/${id}`, data);
    return response.data;
  }

  async deleteHolidayMaster(id: number) {
    const response = await apiClient.delete(`/admin/holiday-master/${id}`);
    return response.data;
  }

  async searchHolidayMasters(holidayName: string) {
    const response = await apiClient.get(
      `/admin/holiday-master/search?name=${encodeURIComponent(holidayName)}`
    );
    return response.data;
  }

  async getHolidayMastersByDateRange(startDate: string, endDate: string) {
    const response = await apiClient.get(
      `/admin/holiday-master/by-date-range?start_date=${startDate}&end_date=${endDate}`
    );
    return response.data;
  }

  async getHolidayMastersByYear(year: number) {
    const response = await apiClient.get(
      `/admin/holiday-master/by-year?year=${year}`
    );
    return response.data;
  }

  async getRecurringHolidayMasters() {
    const response = await apiClient.get("/admin/holiday-master/recurring");
    return response.data;
  }

  async getPublicHolidayMasters() {
    const response = await apiClient.get("/admin/holiday-master/public");
    return response.data;
  }

  async getCompanyHolidayMasters() {
    const response = await apiClient.get("/admin/holiday-master/company");
    return response.data;
  }

  async getUpcomingHolidayMasters(limit: number = 10) {
    const response = await apiClient.get(
      `/admin/holiday-master/upcoming?limit=${limit}`
    );
    return response.data;
  }

  // Employee Experience APIs
  async deleteEmployeeExperience(employeeId: number, experienceId: number) {
    const response = await apiClient.delete(
      `/admin/employee/${employeeId}/experiences/${experienceId}`
    );
    return response.data;
  }

  async uploadEmployeeExperienceDocuments(
    employeeId: number,
    experienceId: number,
    formData: FormData
  ) {
    const response = await apiClient.post(
      `/admin/employee/${employeeId}/experiences/${experienceId}/documents`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  }

  // Employee Detail APIs
  async getEmployeeBasicDetails(employeeId: number) {
    const response = await apiClient.get(
      `/admin/employee/${employeeId}/basic-details`
    );
    return response.data;
  }

  async getEmployeeEmergencyContact(employeeId: number) {
    const response = await apiClient.get(
      `/admin/employee/${employeeId}/emergency-contact`
    );
    return response.data;
  }

  async getEmployeeAddress(employeeId: number) {
    const response = await apiClient.get(
      `/admin/employee/${employeeId}/address`
    );
    return response.data;
  }

  async getEmployeeExperiences(employeeId: number) {
    const response = await apiClient.get(
      `/admin/employee/${employeeId}/experiences`
    );
    return response.data;
  }

  async getEmployeeEducation(employeeId: number) {
    const response = await apiClient.get(
      `/admin/employee/${employeeId}/education`
    );
    return response.data;
  }

  async getEmployeeIdentityDocuments(employeeId: number) {
    const response = await apiClient.get(
      `/admin/employee/${employeeId}/identity-documents`
    );
    return response.data;
  }

  // Resignation APIs
  async applyResignation(employeeId: number, data: any) {
    const response = await apiClient.post(
      `/admin/employee/${employeeId}/resignation/apply`,
      data
    );
    return response.data;
  }

  async withdrawResignation(employeeId: number) {
    const response = await apiClient.post(
      `/admin/employee/${employeeId}/resignation/withdraw`
    );
    return response.data;
  }

  // Exit APIs
  async updateEmployeeExitDetails(employeeId: number, data: any) {
    const response = await apiClient.put(
      `/admin/employee/${employeeId}/exit-details`,
      data
    );
    return response.data;
  }

  async completeEmployeeExit(employeeId: number) {
    const response = await apiClient.post(
      `/admin/employee/${employeeId}/exit/complete`
    );
    return response.data;
  }

  // Employee Update APIs
  async updateEmployeeBasicDetails(employeeId: number, data: any) {
    const response = await apiClient.put(
      `/admin/employee/${employeeId}/basic-details`,
      data
    );
    return response.data;
  }

  async updateEmployeeEmergencyContact(employeeId: number, data: any) {
    const response = await apiClient.put(
      `/admin/employee/${employeeId}/emergency-contact`,
      data
    );
    return response.data;
  }

  async updateEmployeeAddress(employeeId: number, data: any) {
    const response = await apiClient.put(
      `/admin/employee/${employeeId}/address`,
      data
    );
    return response.data;
  }

  async updateEmployeeExperiences(employeeId: number, data: any) {
    const response = await apiClient.put(
      `/admin/employee/${employeeId}/experiences`,
      data
    );
    return response.data;
  }

  async updateEmployeeEducation(employeeId: number, data: any) {
    const response = await apiClient.put(
      `/admin/employee/${employeeId}/education`,
      data
    );
    return response.data;
  }

  async updateEmployeeIdentityDocuments(employeeId: number, data: any) {
    const response = await apiClient.put(
      `/admin/employee/${employeeId}/identity-documents`,
      data
    );
    return response.data;
  }

  // Employee Delete APIs
  async deleteEmployeeEmergencyContact(employeeId: number) {
    const response = await apiClient.delete(
      `/admin/employee/${employeeId}/emergency-contact`
    );
    return response.data;
  }

  async deleteEmployeeAddress(employeeId: number) {
    const response = await apiClient.delete(
      `/admin/employee/${employeeId}/address`
    );
    return response.data;
  }

  async deleteEmployeeEducation(employeeId: number, educationId: number) {
    const response = await apiClient.delete(
      `/admin/employee/${employeeId}/education/${educationId}`
    );
    return response.data;
  }

  async deleteEmployeeIdentityDocument(employeeId: number, documentId: number) {
    const response = await apiClient.delete(
      `/admin/employee/${employeeId}/identity-documents/${documentId}`
    );
    return response.data;
  }

  // File Upload APIs
  async uploadEmployeeProfilePicture(employeeId: number, formData: FormData) {
    const response = await apiClient.post(
      `/admin/employee/${employeeId}/profile-picture`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  }

  async uploadEmployeeDocuments(employeeId: number, formData: FormData) {
    const response = await apiClient.post(
      `/admin/employee/${employeeId}/documents`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  }

  async uploadEmployeeEducationDocuments(
    employeeId: number,
    educationId: number,
    formData: FormData
  ) {
    const response = await apiClient.post(
      `/admin/employee/${employeeId}/education/${educationId}/documents`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  }

  async uploadEmployeeIdentityDocuments(
    employeeId: number,
    formData: FormData
  ) {
    const response = await apiClient.post(
      `/admin/employee/${employeeId}/identity-documents`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  }

  // Document Delete APIs
  async deleteEmployeeDocument(employeeId: number, documentId: number) {
    const response = await apiClient.delete(
      `/admin/employee/${employeeId}/documents/${documentId}`
    );
    return response.data;
  }

  async deleteEmployeeExperienceDocument(
    employeeId: number,
    experienceId: number,
    documentId: number
  ) {
    const response = await apiClient.delete(
      `/admin/employee/${employeeId}/experiences/${experienceId}/documents/${documentId}`
    );
    return response.data;
  }

  async deleteEmployeeEducationDocument(
    employeeId: number,
    educationId: number,
    documentId: number
  ) {
    const response = await apiClient.delete(
      `/admin/employee/${employeeId}/education/${educationId}/documents/${documentId}`
    );
    return response.data;
  }

  async updateEmployeeIdentityDocument(
    employeeId: number,
    documentId: number,
    data: any
  ) {
    const response = await apiClient.put(
      `/admin/employee/${employeeId}/identity-documents/${documentId}`,
      data
    );
    return response.data;
  }

  // Role Management APIs
  async getRoles() {
    const response = await apiClient.get("/roles");
    return response.data;
  }

  async getRoleById(id: number) {
    const response = await apiClient.get(`/roles/${id}`);
    return response.data;
  }

  async createRole(data: any) {
    const response = await apiClient.post("/roles", data);
    return response.data;
  }

  async updateRole(id: number, data: any) {
    const response = await apiClient.put(`/roles/${id}`, data);
    return response.data;
  }

  async deleteRole(id: number) {
    const response = await apiClient.delete(`/roles/${id}`);
    return response.data;
  }

  async assignRoleToEmployee(data: any) {
    const response = await apiClient.post("/employee-roles", data);
    return response.data;
  }

  async removeRoleFromEmployee(employeeId: number, roleId: number) {
    const response = await apiClient.delete(
      `/employee-roles/${employeeId}/${roleId}`
    );
    return response.data;
  }

  async getEmployeeRoles(employeeId: number) {
    const response = await apiClient.get(`/employee-roles/${employeeId}`);
    return response.data;
  }

  async getEmployeesWithRoles() {
    const response = await apiClient.get("/roles/employees");
    return response.data;
  }

  // Permission APIs
  async getPermissions() {
    const response = await apiClient.get("/permissions");
    return response.data;
  }

  async getPermissionById(id: number) {
    const response = await apiClient.get(`/permissions/${id}`);
    return response.data;
  }

  async getPermissionsByModule(module: string) {
    const response = await apiClient.get(`/permissions/module/${module}`);
    return response.data;
  }

  async createPermission(data: any) {
    const response = await apiClient.post("/permissions", data);
    return response.data;
  }

  async updatePermission(id: number, data: any) {
    const response = await apiClient.put(`/permissions/${id}`, data);
    return response.data;
  }

  async deletePermission(id: number) {
    const response = await apiClient.delete(`/permissions/${id}`);
    return response.data;
  }

  async bulkCreatePermissions(data: any) {
    const response = await apiClient.post("/permissions/bulk", data);
    return response.data;
  }

  async getPermissionModules() {
    const response = await apiClient.get("/permissions/modules/list");
    return response.data;
  }

  async getPermissionActions() {
    const response = await apiClient.get("/permissions/actions/list");
    return response.data;
  }

  async getPermissionResources() {
    const response = await apiClient.get("/permissions/resources/list");
    return response.data;
  }

  // Auth APIs
  async login(username: string, password: string) {
    const response = await apiClient.post("/auth/login", {
      username,
      password,
    });
    return response.data;
  }

  async changePassword(data: any) {
    const response = await apiClient.post("/auth/change-password", data);
    return response.data;
  }

  async forgotPassword(username: string) {
    try {
      const response = await apiClient.post("/auth/forgot-password", { user_name: username });
      return response.data;
    } catch (error: any) {
      console.error("Forgot password API error:", error);
      throw new Error(error.response?.data?.message || "Forgot password request failed");
    }
  }

  async refreshToken(refreshToken?: string | null) {
    try {
      console.log("Calling refresh token API...");
      const response = await apiClient.post("/auth/refresh-token", {}, {
        withCredentials: true
      });
      console.log("Refresh token API response:", response.data);
      return response.data;
    } catch (error: any) {
      console.error("Refresh token API error:", error);
      console.error("Error response:", error.response?.data);
      throw new Error(error.response?.data?.message || "Token refresh failed");
    }
  }

  async getEmployeeById(id: number | string) {
    try {
      const response = await apiClient.get(`/admin/employee/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching employee details:", error);
      throw error;
    }
  }

  async resendInvite(employeeId: number | string) {
    try {
      const response = await apiClient.post("/auth/resend-invite", { employee_id: employeeId });
      return response.data;
    } catch (error: any) {
      console.error("Error resending invite:", error);
      throw new Error(error.response?.data?.message || "Failed to resend invite");
    }
  }

  async getEmployees(params?: any) {
    try {
      const response = await apiClient.get("/admin/employee", { params });
      return response.data;
    } catch (error) {
      console.error("Error fetching employees:", error);
      throw error;
    }
  }

  async logout() {
    try {
      const response = await apiClient.post("/auth/logout");
      return response.data;
    } catch (error: any) {
      // Even if logout API fails, we should still clear local storage
      console.error("Logout API error:", error);
      return { success: true, message: "Logged out successfully" };
    }
  }

  // Reimbursement Type APIs
  async getReimbursementTypes() {
    const response = await apiClient.get("/admin/reimbursement-types");
    return response.data;
  }

  async getActiveReimbursementTypes() {
    const response = await apiClient.get("/admin/reimbursement-types/active");
    return response.data;
  }

  async getReimbursementTypeById(id: number) {
    const response = await apiClient.get(`/admin/reimbursement-types/${id}`);
    return response.data;
  }

  async createReimbursementType(data: any) {
    const response = await apiClient.post(
      "/admin/reimbursement-types/create",
      data
    );
    return response.data;
  }

  async updateReimbursementType(id: number, data: any) {
    const response = await apiClient.put(
      `/admin/reimbursement-types/${id}`,
      data
    );
    return response.data;
  }

  async deleteReimbursementType(id: number) {
    const response = await apiClient.delete(`/admin/reimbursement-types/${id}`);
    return response.data;
  }

  async searchReimbursementTypes(typeName: string) {
    const response = await apiClient.get(
      `/admin/reimbursement-types/search/type?type_name=${encodeURIComponent(
        typeName
      )}`
    );
    return response.data;
  }

  async getReimbursementTypesByApproval(requiresApproval: boolean) {
    const response = await apiClient.get(
      `/admin/reimbursement-types/filter/approval?requires_approval=${requiresApproval}`
    );
    return response.data;
  }

  async getReimbursementTypesByMaxAmount(maxAmount: number) {
    const response = await apiClient.get(
      `/admin/reimbursement-types/filter/max-amount?max_amount=${maxAmount}`
    );
    return response.data;
  }

  // Employee Reimbursement APIs
  async getEmployeeReimbursements() {
    const response = await apiClient.get("/admin/employee-reimbursements");
    return response.data;
  }

  async getEmployeeReimbursementById(id: number) {
    const response = await apiClient.get(
      `/admin/employee-reimbursements/${id}`
    );
    return response.data;
  }

  async createEmployeeReimbursement(data: any) {
    const response = await apiClient.post(
      "/admin/employee-reimbursements",
      data
    );
    return response.data;
  }

  async updateEmployeeReimbursement(id: number, data: any) {
    const response = await apiClient.put(
      `/admin/employee-reimbursements/${id}`,
      data
    );
    return response.data;
  }

  async deleteEmployeeReimbursement(id: number) {
    const response = await apiClient.delete(
      `/admin/employee-reimbursements/${id}`
    );
    return response.data;
  }

  async getEmployeeReimbursementsByEmployee(employeeId: number) {
    const response = await apiClient.get(
      `/admin/employee-reimbursements/employee/${employeeId}`
    );
    return response.data;
  }

  async getPendingEmployeeReimbursements() {
    const response = await apiClient.get(
      "/admin/employee-reimbursements/pending"
    );
    return response.data;
  }

  async approveEmployeeReimbursement(id: number, data: any) {
    const response = await apiClient.post(
      `/admin/employee-reimbursements/${id}/approve`,
      data
    );
    return response.data;
  }

  async rejectEmployeeReimbursement(id: number, data: any) {
    const response = await apiClient.post(
      `/admin/employee-reimbursements/${id}/reject`,
      data
    );
    return response.data;
  }

  async markEmployeeReimbursementAsPaid(id: number, data: any) {
    const response = await apiClient.post(
      `/admin/employee-reimbursements/${id}/mark-paid`,
      data
    );
    return response.data;
  }

  async searchEmployeeReimbursements(searchType: string, searchTerm: string) {
    const response = await apiClient.get(
      `/admin/employee-reimbursements/search?search_type=${searchType}&search_term=${encodeURIComponent(
        searchTerm
      )}`
    );
    return response.data;
  }

  async getEmployeeReimbursementsByStatus(status: string) {
    const response = await apiClient.get(
      `/admin/employee-reimbursements/status/${status}`
    );
    return response.data;
  }

  async getEmployeeReimbursementsByDateRange(
    startDate: string,
    endDate: string
  ) {
    const response = await apiClient.get(
      `/admin/employee-reimbursements/filter/date-range?start_date=${startDate}&end_date=${endDate}`
    );
    return response.data;
  }

  async getEmployeeReimbursementsByAmountRange(
    minAmount: number,
    maxAmount: number
  ) {
    const response = await apiClient.get(
      `/admin/employee-reimbursements/filter/amount-range?min_amount=${minAmount}&max_amount=${maxAmount}`
    );
    return response.data;
  }

  // Attendance Record APIs
  async getAttendanceRecords() {
    const response = await apiClient.get("/admin/attendance-records");
    return response.data;
  }

  async getAttendanceRecordById(id: number) {
    const response = await apiClient.get(`/admin/attendance-records/${id}`);
    return response.data;
  }

  async createAttendanceRecord(data: any) {
    const response = await apiClient.post("/admin/attendance-records", data);
    return response.data;
  }

  async updateAttendanceRecord(id: number, data: any) {
    const response = await apiClient.put(
      `/admin/attendance-records/${id}`,
      data
    );
    return response.data;
  }

  async deleteAttendanceRecord(id: number) {
    const response = await apiClient.delete(`/admin/attendance-records/${id}`);
    return response.data;
  }

  async getAttendanceRecordsByEmployee(employeeId: number) {
    const response = await apiClient.get(
      `/admin/attendance-records/employee/${employeeId}`
    );
    return response.data;
  }

  async getPendingApprovalAttendanceRecords() {
    const response = await apiClient.get(
      "/admin/attendance-records/pending-approval"
    );
    return response.data;
  }

  async approveAttendanceRecord(id: number, data: any) {
    const response = await apiClient.post(
      `/admin/attendance-records/${id}/approve`,
      data
    );
    return response.data;
  }

  async rejectAttendanceRecord(id: number, data: any) {
    const response = await apiClient.post(
      `/admin/attendance-records/${id}/reject`,
      data
    );
    return response.data;
  }

  async punchIn(data: any) {
    const response = await apiClient.post(
      "/admin/attendance-records/punch-in",
      data
    );
    return response.data;
  }

  async punchOut(data: any) {
    const response = await apiClient.post(
      "/admin/attendance-records/punch-out",
      data
    );
    return response.data;
  }

  async searchAttendanceRecords(searchType: string, searchTerm: string) {
    const response = await apiClient.get(
      `/admin/attendance-records/search?search_type=${searchType}&search_term=${encodeURIComponent(
        searchTerm
      )}`
    );
    return response.data;
  }

  async getAttendanceRecordsByStatus(status: string) {
    const response = await apiClient.get(
      `/admin/attendance-records/status/${status}`
    );
    return response.data;
  }

  async getAttendanceRecordsByDateRange(startDate: string, endDate: string) {
    const response = await apiClient.get(
      `/admin/attendance-records/filter/date-range?start_date=${startDate}&end_date=${endDate}`
    );
    return response.data;
  }

  async getAttendanceRecordsByHoursRange(minHours: number, maxHours: number) {
    const response = await apiClient.get(
      `/admin/attendance-records/filter/hours-range?min_hours=${minHours}&max_hours=${maxHours}`
    );
    return response.data;
  }

  async getAttendanceSummary(
    employeeId: number,
    startDate: string,
    endDate: string
  ) {
    const response = await apiClient.get(
      `/admin/attendance-records/summary/${employeeId}?start_date=${startDate}&end_date=${endDate}`
    );
    return response.data;
  }

  // Payroll Component APIs
  async getPayrollComponents() {
    const response = await apiClient.get("/admin/payroll-components");
    return response.data;
  }

  async getPayrollComponentById(id: number) {
    const response = await apiClient.get(`/admin/payroll-components/${id}`);
    return response.data;
  }

  async createPayrollComponent(data: any) {
    const response = await apiClient.post("/admin/payroll-components", data);
    return response.data;
  }

  async updatePayrollComponent(id: number, data: any) {
    const response = await apiClient.put(
      `/admin/payroll-components/${id}`,
      data
    );
    return response.data;
  }

  async deletePayrollComponent(id: number) {
    const response = await apiClient.delete(`/admin/payroll-components/${id}`);
    return response.data;
  }

  async getPayrollComponentStats() {
    const response = await apiClient.get("/admin/payroll-components/stats");
    return response.data;
  }

  async getPayrollComponentsByFilters(filters: any) {
    const params = new URLSearchParams();
    Object.keys(filters).forEach((key) => {
      if (filters[key] !== undefined && filters[key] !== "") {
        params.append(key, filters[key].toString());
      }
    });
    const response = await apiClient.get(
      `/admin/payroll-components/filter?${params.toString()}`
    );
    return response.data;
  }

  async searchPayrollComponents(name: string) {
    const response = await apiClient.get(
      `/admin/payroll-components/search?name=${encodeURIComponent(name)}`
    );
    return response.data;
  }

  async getPayrollComponentsForPayslip() {
    const response = await apiClient.get("/admin/payroll-components/payslip");
    return response.data;
  }

  async getPayrollComponentsByType(type: "Earning" | "Deduction") {
    const response = await apiClient.get(
      `/admin/payroll-components/type/${type}`
    );
    return response.data;
  }

  async getPayrollEarnings() {
    const response = await apiClient.get("/admin/payroll-components/earnings");
    return response.data;
  }

  async getPayrollDeductions() {
    const response = await apiClient.get(
      "/admin/payroll-components/deductions"
    );
    return response.data;
  }

  async getTaxablePayrollComponents() {
    const response = await apiClient.get("/admin/payroll-components/taxable");
    return response.data;
  }

  async getStatutoryPayrollComponents() {
    const response = await apiClient.get("/admin/payroll-components/statutory");
    return response.data;
  }

  async getPayrollComponentsByCalculationType(calculationType: string) {
    const response = await apiClient.get(
      `/admin/payroll-components/calculation/${calculationType}`
    );
    return response.data;
  }

  async reorderPayrollComponents(componentIds: number[]) {
    const response = await apiClient.post("/admin/payroll-components/reorder", {
      componentIds,
    });
    return response.data;
  }

  // Employee Salary Structures APIs
  async getEmployeeSalaryStructures() {
    const response = await apiClient.get("/admin/employee-salary-structures");
    return response.data;
  }

  async getEmployeeSalaryStructureById(id: number) {
    const response = await apiClient.get(
      `/admin/employee-salary-structures/${id}`
    );
    return response.data;
  }

  async createEmployeeSalaryStructure(data: any) {
    const response = await apiClient.post(
      "/admin/employee-salary-structures",
      data
    );
    return response.data;
  }

  async updateEmployeeSalaryStructure(id: number, data: any) {
    const response = await apiClient.put(
      `/admin/employee-salary-structures/${id}`,
      data
    );
    return response.data;
  }

  async deleteEmployeeSalaryStructure(id: number) {
    const response = await apiClient.delete(
      `/admin/employee-salary-structures/${id}`
    );
    return response.data;
  }

  async getEmployeeSalaryStructureStats() {
    const response = await apiClient.get(
      "/admin/employee-salary-structures/stats"
    );
    return response.data;
  }

  async searchEmployeeSalaryStructures(searchTerm: string) {
    const response = await apiClient.get(
      `/admin/employee-salary-structures/search?search_term=${encodeURIComponent(
        searchTerm
      )}`
    );
    return response.data;
  }

  async getEmployeeSalaryStructuresByEmployeeId(employeeId: number) {
    const response = await apiClient.get(
      `/admin/employee-salary-structures/employee/${employeeId}`
    );
    return response.data;
  }

  async getActiveEmployeeSalaryStructuresByEmployeeId(employeeId: number) {
    const response = await apiClient.get(
      `/admin/employee-salary-structures/employee/${employeeId}/active`
    );
    return response.data;
  }

  async getEmployeeSalaryStructureHistory(
    employeeId: number,
    componentId: number
  ) {
    const response = await apiClient.get(
      `/admin/employee-salary-structures/history/${employeeId}/${componentId}`
    );
    return response.data;
  }

  async getEmployeeSalaryStructuresByDateRange(
    startDate: string,
    endDate: string
  ) {
    const response = await apiClient.get(
      `/admin/employee-salary-structures/date-range?start_date=${startDate}&end_date=${endDate}`
    );
    return response.data;
  }

  // Salary Template APIs
  async getSalaryTemplates() {
    const response = await apiClient.get("/admin/salary-templates");
    return response.data;
  }

  async getSalaryTemplateById(id: number) {
    const response = await apiClient.get(`/admin/salary-templates/${id}`);
    return response.data;
  }

  async createSalaryTemplate(data: any) {
    const response = await apiClient.post("/admin/salary-templates", data);
    return response.data;
  }

  async updateSalaryTemplate(id: number, data: any) {
    const response = await apiClient.put(`/admin/salary-templates/${id}`, data);
    return response.data;
  }

  async deleteSalaryTemplate(id: number) {
    const response = await apiClient.delete(`/admin/salary-templates/${id}`);
    return response.data;
  }

  // Template Component APIs
  async getTemplateComponents(templateId: number) {
    const response = await apiClient.get(
      `/admin/salary-templates/${templateId}/components`
    );
    return response.data;
  }

  async createTemplateComponent(templateId: number, data: any) {
    const response = await apiClient.post(
      `/admin/salary-templates/${templateId}/components`,
      data
    );
    return response.data;
  }

  async updateTemplateComponent(
    templateId: number,
    componentId: number,
    data: any
  ) {
    const response = await apiClient.put(
      `/admin/salary-templates/${templateId}/components/${componentId}`,
      data
    );
    return response.data;
  }

  async deleteTemplateComponent(templateId: number, componentId: number) {
    const response = await apiClient.delete(
      `/admin/salary-templates/${templateId}/components/${componentId}`
    );
    return response.data;
  }

  // Employee Template Assignment APIs
  async getEmployeeTemplateAssignments() {
    const response = await apiClient.get(
      "/admin/employee-template-assignments"
    );
    return response.data;
  }

  async getEmployeeTemplateAssignmentById(id: number) {
    const response = await apiClient.get(
      `/admin/employee-template-assignments/${id}`
    );
    return response.data;
  }

  async createEmployeeTemplateAssignment(data: any) {
    const response = await apiClient.post(
      "/admin/employee-template-assignments",
      data
    );
    return response.data;
  }

  async updateEmployeeTemplateAssignment(id: number, data: any) {
    const response = await apiClient.put(
      `/admin/employee-template-assignments/${id}`,
      data
    );
    return response.data;
  }

  async deleteEmployeeTemplateAssignment(id: number) {
    const response = await apiClient.delete(
      `/admin/employee-template-assignments/${id}`
    );
    return response.data;
  }

  async getEmployeeTemplateAssignmentsByEmployeeId(employeeId: number) {
    const response = await apiClient.get(
      `/admin/employee-template-assignments/employee/${employeeId}`
    );
    return response.data;
  }

  async generateSalaryStructureFromTemplate(
    employeeId: number,
    templateId: number,
    ctc: number
  ) {
    const response = await apiClient.post(
      `/admin/employees/${employeeId}/generate-structure`,
      {
        template_id: templateId,
        ctc: ctc,
      }
    );
    return response.data;
  }

  // System Config APIs
  async getSystemConfigs() {
    const response = await apiClient.get("/admin/system-config");
    return response.data;
  }

  async getSystemConfigById(id: number) {
    const response = await apiClient.get(`/admin/system-config/${id}`);
    return response.data;
  }

  async getSystemConfigByKey(key: string) {
    const response = await apiClient.get(`/admin/system-config/key/${key}`);
    return response.data;
  }

  async getSystemConfigsByModule(module: string) {
    const response = await apiClient.get(
      `/admin/system-config/module/${module}`
    );
    return response.data;
  }

  async searchSystemConfigs(key: string) {
    const response = await apiClient.get(
      `/admin/system-config/search?key=${encodeURIComponent(key)}`
    );
    return response.data;
  }

  async createSystemConfig(data: any) {
    const response = await apiClient.post("/admin/system-config", data);
    return response.data;
  }

  async updateSystemConfig(id: number, data: any) {
    const response = await apiClient.put(`/admin/system-config/${id}`, data);
    return response.data;
  }

  async deleteSystemConfig(id: number) {
    const response = await apiClient.delete(`/admin/system-config/${id}`);
    return response.data;
  }

  // Tenant Config APIs (theme + branding for payslip, quotation, PO, sales order)
  // SUPER_ADMIN: pass tenantId to get/update any tenant's config
  async getTenantConfig(tenantId?: number) {
    const url = tenantId != null ? `/admin/tenant-config?tenant_id=${tenantId}` : "/admin/tenant-config";
    const response = await apiClient.get(url);
    return response.data;
  }

  async updateTenantConfig(data: Record<string, any>, tenantId?: number) {
    const payload = tenantId != null ? { ...data, tenant_id: tenantId } : data;
    const response = await apiClient.put("/admin/tenant-config", payload);
    return response.data;
  }

  // Client APIs
  async getClients() {
    const response = await apiClient.get("/admin/clients");
    return response.data;
  }

  async getClientById(id: number) {
    const response = await apiClient.get(`/admin/clients/${id}`);
    return response.data;
  }

  async searchClients(query: string, status?: string) {
    const params = new URLSearchParams();
    if (query) params.append("name", query);
    if (query) params.append("code", query);
    if (status) params.append("status", status);
    const response = await apiClient.get(
      `/admin/clients/search?${params.toString()}`
    );
    return response.data;
  }

  async createClient(data: any) {
    const response = await apiClient.post("/admin/clients", data);
    return response.data;
  }

  async updateClient(id: number, data: any) {
    const response = await apiClient.put(`/admin/clients/${id}`, data);
    return response.data;
  }

  async deleteClient(id: number) {
    const response = await apiClient.delete(`/admin/clients/${id}`);
    return response.data;
  }

  // Lead Source APIs
  async getLeadSources() {
    const response = await apiClient.get("/admin/lead-sources");
    return response.data;
  }
  async createLeadSource(data: any) {
    const response = await apiClient.post("/admin/lead-sources", data);
    return response.data;
  }
  async updateLeadSource(id: number, data: any) {
    const response = await apiClient.put(`/admin/lead-sources/${id}`, data);
    return response.data;
  }
  async deleteLeadSource(id: number) {
    const response = await apiClient.delete(`/admin/lead-sources/${id}`);
    return response.data;
  }

  // Lead APIs
  async getLeads(params?: { status?: string }) {
    const query = params?.status ? `?status=${params.status}` : "";
    const response = await apiClient.get(`/admin/leads${query}`);
    return response.data;
  }
  async getLeadById(id: number) {
    const response = await apiClient.get(`/admin/leads/${id}`);
    return response.data;
  }
  async createLead(data: any) {
    const response = await apiClient.post("/admin/leads", data);
    return response.data;
  }
  async updateLead(id: number, data: any) {
    const response = await apiClient.put(`/admin/leads/${id}`, data);
    return response.data;
  }
  async deleteLead(id: number) {
    const response = await apiClient.delete(`/admin/leads/${id}`);
    return response.data;
  }

  // Opportunity APIs
  async getOpportunities(params?: { stage?: string }) {
    const query = params?.stage ? `?stage=${params.stage}` : "";
    const response = await apiClient.get(`/admin/opportunities${query}`);
    return response.data;
  }
  async getOpportunityById(id: number) {
    const response = await apiClient.get(`/admin/opportunities/${id}`);
    return response.data;
  }
  async createOpportunity(data: any) {
    const response = await apiClient.post("/admin/opportunities", data);
    return response.data;
  }
  async updateOpportunity(id: number, data: any) {
    const response = await apiClient.put(`/admin/opportunities/${id}`, data);
    return response.data;
  }
  async deleteOpportunity(id: number) {
    const response = await apiClient.delete(`/admin/opportunities/${id}`);
    return response.data;
  }

  // Product APIs
  async getProducts(params?: { type?: string; status?: string }) {
    const queryParams = new URLSearchParams();
    if (params?.type && params.type !== "all") queryParams.append("type", params.type);
    if (params?.status && params.status !== "all") queryParams.append("status", params.status);
    const query = queryParams.toString() ? `?${queryParams.toString()}` : "";
    const response = await apiClient.get(`/admin/products${query}`);
    return response.data;
  }
  async getProductById(id: number) {
    const response = await apiClient.get(`/admin/products/${id}`);
    return response.data;
  }
  async createProduct(data: any) {
    const response = await apiClient.post("/admin/products", data);
    return response.data;
  }
  async updateProduct(id: number, data: any) {
    const response = await apiClient.put(`/admin/products/${id}`, data);
    return response.data;
  }
  async deleteProduct(id: number) {
    const response = await apiClient.delete(`/admin/products/${id}`);
    return response.data;
  }

  // Notification APIs
  async getNotifications(params?: { is_read?: boolean; type?: string; module?: string; limit?: number; offset?: number }) {
    const queryParams = new URLSearchParams();
    if (params?.is_read !== undefined) queryParams.append('is_read', String(params.is_read));
    if (params?.type) queryParams.append('type', params.type);
    if (params?.module) queryParams.append('module', params.module);
    if (params?.limit) queryParams.append('limit', String(params.limit));
    if (params?.offset) queryParams.append('offset', String(params.offset));

    const url = `/admin/notifications${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  }

  async getNotificationById(id: number) {
    const response = await apiClient.get(`/admin/notifications/${id}`);
    return response.data;
  }

  async getUnreadNotificationCount() {
    const response = await apiClient.get('/admin/notifications/unread/count');
    return response.data;
  }

  async markNotificationAsRead(id: number) {
    const response = await apiClient.put(`/admin/notifications/${id}/read`);
    return response.data;
  }

  async markAllNotificationsAsRead() {
    const response = await apiClient.put('/admin/notifications/read-all');
    return response.data;
  }

  async deleteNotification(id: number) {
    const response = await apiClient.delete(`/admin/notifications/${id}`);
    return response.data;
  }

  async createNotification(data: any) {
    const response = await apiClient.post('/admin/notifications', data);
    return response.data;
  }

  // Task APIs
  async getTasks(params?: { project_id?: number; status?: string }) {
    const queryParams = new URLSearchParams();
    if (params?.project_id) queryParams.append('project_id', String(params.project_id));
    if (params?.status) queryParams.append('status', params.status);

    const url = `/admin/tasks${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  }

  async getTasksByProject(projectId: number) {
    const response = await apiClient.get(`/admin/tasks/project/${projectId}`);
    return response.data;
  }

  async getTaskById(id: number) {
    const response = await apiClient.get(`/admin/tasks/${id}`);
    return response.data;
  }

  async searchTasks(query: string, projectId?: number, status?: string) {
    const params = new URLSearchParams();
    if (query) params.append('name', query);
    if (projectId) params.append('project_id', String(projectId));
    if (status) params.append('status', status);
    const response = await apiClient.get(`/admin/tasks/search?${params.toString()}`);
    return response.data;
  }

  async createTask(data: any) {
    const response = await apiClient.post('/admin/tasks', data);
    return response.data;
  }

  async updateTask(id: number, data: any) {
    const response = await apiClient.put(`/admin/tasks/${id}`, data);
    return response.data;
  }

  async deleteTask(id: number) {
    const response = await apiClient.delete(`/admin/tasks/${id}`);
    return response.data;
  }

  // Performance Review APIs
  async getPerformanceReviews(params?: { employee_id?: number; status?: string; review_type?: string }) {
    const queryParams = new URLSearchParams();
    if (params?.employee_id) queryParams.append('employee_id', String(params.employee_id));
    if (params?.status) queryParams.append('status', params.status);
    if (params?.review_type) queryParams.append('review_type', params.review_type);

    const url = `/admin/performance-reviews${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  }

  async getPerformanceReviewById(id: number) {
    const response = await apiClient.get(`/admin/performance-reviews/${id}`);
    return response.data;
  }

  async getPerformanceReviewsByEmployee(employeeId: number) {
    const response = await apiClient.get(`/admin/performance-reviews/employee/${employeeId}`);
    return response.data;
  }

  async createPerformanceReview(data: any) {
    const response = await apiClient.post('/admin/performance-reviews', data);
    return response.data;
  }

  async updatePerformanceReview(id: number, data: any) {
    const response = await apiClient.put(`/admin/performance-reviews/${id}`, data);
    return response.data;
  }

  async deletePerformanceReview(id: number) {
    const response = await apiClient.delete(`/admin/performance-reviews/${id}`);
    return response.data;
  }

  // Performance Goal APIs
  async getPerformanceGoals(params?: { employee_id?: number; status?: string; review_id?: number }) {
    const queryParams = new URLSearchParams();
    if (params?.employee_id) queryParams.append('employee_id', String(params.employee_id));
    if (params?.status) queryParams.append('status', params.status);
    if (params?.review_id) queryParams.append('review_id', String(params.review_id));

    const url = `/admin/performance-goals${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  }

  async getPerformanceGoalById(id: number) {
    const response = await apiClient.get(`/admin/performance-goals/${id}`);
    return response.data;
  }

  async getPerformanceGoalsByEmployee(employeeId: number) {
    const response = await apiClient.get(`/admin/performance-goals/employee/${employeeId}`);
    return response.data;
  }

  async createPerformanceGoal(data: any) {
    const response = await apiClient.post('/admin/performance-goals', data);
    return response.data;
  }

  async updatePerformanceGoal(id: number, data: any) {
    const response = await apiClient.put(`/admin/performance-goals/${id}`, data);
    return response.data;
  }

  async deletePerformanceGoal(id: number) {
    const response = await apiClient.delete(`/admin/performance-goals/${id}`);
    return response.data;
  }

  // Job Posting APIs
  async getJobPostings(params?: { status?: string; department_id?: number; employment_type?: string }) {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.department_id) queryParams.append('department_id', String(params.department_id));
    if (params?.employment_type) queryParams.append('employment_type', params.employment_type);

    const url = `/admin/job-postings${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  }

  async getJobPostingById(id: number) {
    const response = await apiClient.get(`/admin/job-postings/${id}`);
    return response.data;
  }

  async createJobPosting(data: any) {
    const response = await apiClient.post('/admin/job-postings', data);
    return response.data;
  }

  async updateJobPosting(id: number, data: any) {
    const response = await apiClient.put(`/admin/job-postings/${id}`, data);
    return response.data;
  }

  async deleteJobPosting(id: number) {
    const response = await apiClient.delete(`/admin/job-postings/${id}`);
    return response.data;
  }

  // Job Application APIs
  async getJobApplications(params?: { posting_id?: number; status?: string; current_stage?: string }) {
    const queryParams = new URLSearchParams();
    if (params?.posting_id) queryParams.append('posting_id', String(params.posting_id));
    if (params?.status) queryParams.append('status', params.status);
    if (params?.current_stage) queryParams.append('current_stage', params.current_stage);

    const url = `/admin/job-applications${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  }

  async getJobApplicationById(id: number) {
    const response = await apiClient.get(`/admin/job-applications/${id}`);
    return response.data;
  }

  async createJobApplication(data: any) {
    const response = await apiClient.post('/admin/job-applications', data);
    return response.data;
  }

  async updateJobApplication(id: number, data: any) {
    const response = await apiClient.put(`/admin/job-applications/${id}`, data);
    return response.data;
  }

  async deleteJobApplication(id: number) {
    const response = await apiClient.delete(`/admin/job-applications/${id}`);
    return response.data;
  }

  // Interview Feedback APIs
  async getInterviewFeedbacks(params?: { application_id?: number; reviewer_id?: number }) {
    const queryParams = new URLSearchParams();
    if (params?.application_id) queryParams.append('application_id', String(params.application_id));
    if (params?.reviewer_id) queryParams.append('reviewer_id', String(params.reviewer_id));
    const url = `/admin/interview-feedbacks${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  }

  async getInterviewFeedbackById(id: number) {
    const response = await apiClient.get(`/admin/interview-feedbacks/${id}`);
    return response.data;
  }

  async createInterviewFeedback(data: any) {
    const response = await apiClient.post('/admin/interview-feedbacks', data);
    return response.data;
  }

  async updateInterviewFeedback(id: number, data: any) {
    const response = await apiClient.put(`/admin/interview-feedbacks/${id}`, data);
    return response.data;
  }

  async deleteInterviewFeedback(id: number) {
    const response = await apiClient.delete(`/admin/interview-feedbacks/${id}`);
    return response.data;
  }

  // Public Careers (no auth)
  async getPublicCareersJobs(params?: { department_id?: number; location?: string; employment_type?: string }) {
    const queryParams = new URLSearchParams();
    if (params?.department_id) queryParams.append('department_id', String(params.department_id));
    if (params?.location) queryParams.append('location', params.location);
    if (params?.employment_type) queryParams.append('employment_type', params.employment_type);
    const url = `/public/careers/jobs${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  }

  async getPublicCareersJobById(id: number) {
    const response = await apiClient.get(`/public/careers/jobs/${id}`);
    return response.data;
  }

  async submitPublicCareersApplication(data: {
    posting_id: number;
    candidate_name: string;
    candidate_email: string;
    candidate_phone: string;
    cover_letter?: string;
    resume_path?: string;
  }) {
    const response = await apiClient.post('/public/careers/apply', data);
    return response.data;
  }

  // Training Program APIs
  async getTrainingPrograms(params?: { status?: string; department_id?: number; training_type?: string }) {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.department_id) queryParams.append('department_id', String(params.department_id));
    if (params?.training_type) queryParams.append('training_type', params.training_type);

    const url = `/admin/training-programs${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  }

  async getTrainingProgramById(id: number) {
    const response = await apiClient.get(`/admin/training-programs/${id}`);
    return response.data;
  }

  async createTrainingProgram(data: any) {
    const response = await apiClient.post('/admin/training-programs', data);
    return response.data;
  }

  async updateTrainingProgram(id: number, data: any) {
    const response = await apiClient.put(`/admin/training-programs/${id}`, data);
    return response.data;
  }

  async deleteTrainingProgram(id: number) {
    const response = await apiClient.delete(`/admin/training-programs/${id}`);
    return response.data;
  }

  // Employee Training APIs
  async getEmployeeTrainings(params?: { employee_id?: number; program_id?: number; status?: string }) {
    const queryParams = new URLSearchParams();
    if (params?.employee_id) queryParams.append('employee_id', String(params.employee_id));
    if (params?.program_id) queryParams.append('program_id', String(params.program_id));
    if (params?.status) queryParams.append('status', params.status);

    const url = `/admin/employee-trainings${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  }

  async getEmployeeTrainingById(id: number) {
    const response = await apiClient.get(`/admin/employee-trainings/${id}`);
    return response.data;
  }

  async getEmployeeTrainingsByEmployee(employeeId: number) {
    const response = await apiClient.get(`/admin/employee-trainings/employee/${employeeId}`);
    return response.data;
  }

  async createEmployeeTraining(data: any) {
    const response = await apiClient.post('/admin/employee-trainings', data);
    return response.data;
  }

  async updateEmployeeTraining(id: number, data: any) {
    const response = await apiClient.put(`/admin/employee-trainings/${id}`, data);
    return response.data;
  }

  async deleteEmployeeTraining(id: number) {
    const response = await apiClient.delete(`/admin/employee-trainings/${id}`);
    return response.data;
  }

  // Leave Encashment APIs
  async getLeaveEncashments(params?: { employee_id?: number; status?: string; encashment_year?: number }) {
    const queryParams = new URLSearchParams();
    if (params?.employee_id) queryParams.append('employee_id', String(params.employee_id));
    if (params?.status) queryParams.append('status', params.status);
    if (params?.encashment_year) queryParams.append('encashment_year', String(params.encashment_year));

    const url = `/admin/leave-encashments${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  }

  async getLeaveEncashmentById(id: number) {
    const response = await apiClient.get(`/admin/leave-encashments/${id}`);
    return response.data;
  }

  async createLeaveEncashment(data: any) {
    const response = await apiClient.post('/admin/leave-encashments', data);
    return response.data;
  }

  async updateLeaveEncashment(id: number, data: any) {
    const response = await apiClient.put(`/admin/leave-encashments/${id}`, data);
    return response.data;
  }

  async deleteLeaveEncashment(id: number) {
    const response = await apiClient.delete(`/admin/leave-encashments/${id}`);
    return response.data;
  }

  // Leave Carry Forward APIs
  async getLeaveCarryForwards(params?: { employee_id?: number; status?: string; from_year?: number; to_year?: number }) {
    const queryParams = new URLSearchParams();
    if (params?.employee_id) queryParams.append('employee_id', String(params.employee_id));
    if (params?.status) queryParams.append('status', params.status);
    if (params?.from_year) queryParams.append('from_year', String(params.from_year));
    if (params?.to_year) queryParams.append('to_year', String(params.to_year));

    const url = `/admin/leave-carry-forwards${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  }

  async getLeaveCarryForwardById(id: number) {
    const response = await apiClient.get(`/admin/leave-carry-forwards/${id}`);
    return response.data;
  }

  async getLeaveCarryForwardsByEmployee(employeeId: number) {
    const response = await apiClient.get(`/admin/leave-carry-forwards/employee/${employeeId}`);
    return response.data;
  }

  async createLeaveCarryForward(data: any) {
    const response = await apiClient.post('/admin/leave-carry-forwards', data);
    return response.data;
  }

  async updateLeaveCarryForward(id: number, data: any) {
    const response = await apiClient.put(`/admin/leave-carry-forwards/${id}`, data);
    return response.data;
  }

  async deleteLeaveCarryForward(id: number) {
    const response = await apiClient.delete(`/admin/leave-carry-forwards/${id}`);
    return response.data;
  }

  // Tenant APIs
  async getTenants(params?: { status?: string; is_active?: boolean; is_trial?: boolean }) {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.is_active !== undefined) queryParams.append('is_active', String(params.is_active));
    if (params?.is_trial !== undefined) queryParams.append('is_trial', String(params.is_trial));

    const url = `/admin/tenants${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  }

  async getTenantById(id: number) {
    const response = await apiClient.get(`/admin/tenants/${id}`);
    return response.data;
  }

  async createTenant(data: any) {
    const response = await apiClient.post('/admin/tenants', data);
    return response.data;
  }

  async updateTenant(id: number, data: any) {
    const response = await apiClient.put(`/admin/tenants/${id}`, data);
    return response.data;
  }

  async deleteTenant(id: number) {
    const response = await apiClient.delete(`/admin/tenants/${id}`);
    return response.data;
  }

  async activateTenant(id: number) {
    const response = await apiClient.post(`/admin/tenants/${id}/activate`);
    return response.data;
  }

  async suspendTenant(id: number) {
    const response = await apiClient.post(`/admin/tenants/${id}/suspend`);
    return response.data;
  }

  async cancelTenant(id: number) {
    const response = await apiClient.post(`/admin/tenants/${id}/cancel`);
    return response.data;
  }

  async getTenantUsage(id: number) {
    const response = await apiClient.get(`/admin/tenants/${id}/usage`);
    return response.data;
  }

  // Subscription APIs
  async getCurrentSubscription() {
    const response = await apiClient.get('/admin/subscriptions/current');
    return response.data;
  }

  async getSubscriptionHistory(params?: { status?: string; limit?: number }) {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.limit) queryParams.append('limit', String(params.limit));

    const url = `/admin/subscriptions/history${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get(url);
    return response.data;
  }

  async getSubscriptionById(id: number) {
    const response = await apiClient.get(`/admin/subscriptions/${id}`);
    return response.data;
  }

  async createSubscription(data: any) {
    const response = await apiClient.post('/admin/subscriptions', data);
    return response.data;
  }

  async updateSubscription(id: number, data: any) {
    const response = await apiClient.put(`/admin/subscriptions/${id}`, data);
    return response.data;
  }

  async cancelSubscription(id: number) {
    const response = await apiClient.post(`/admin/subscriptions/${id}/cancel`);
    return response.data;
  }

  async renewSubscription(id: number) {
    const response = await apiClient.post(`/admin/subscriptions/${id}/renew`);
    return response.data;
  }

  async upgradeSubscription(id: number, data: { new_plan_id: number; billing_cycle?: string }) {
    const response = await apiClient.post(`/admin/subscriptions/${id}/upgrade`, data);
    return response.data;
  }

  async downgradeSubscription(id: number, data: { new_plan_id: number; billing_cycle?: string }) {
    const response = await apiClient.post(`/admin/subscriptions/${id}/downgrade`, data);
    return response.data;
  }

  // Subscription Plan APIs
  async getSubscriptionPlans() {
    const response = await apiClient.get('/admin/subscription-plans');
    return response.data;
  }

  async getSubscriptionPlanById(id: number) {
    const response = await apiClient.get(`/admin/subscription-plans/${id}`);
    return response.data;
  }

  async getActiveSubscriptionPlans() {
    const response = await apiClient.get('/admin/subscription-plans/active/plans');
    return response.data;
  }

  // Plan Permission APIs
  async getPlansWithPermissions() {
    const response = await apiClient.get('/admin/plan-permissions/plans');
    return response.data;
  }

  async getPlanPermissions(planId: number) {
    const response = await apiClient.get(`/admin/plan-permissions/plan/${planId}`);
    return response.data;
  }

  async getAvailablePermissions() {
    const response = await apiClient.get('/admin/plan-permissions/permissions');
    return response.data;
  }

  async assignPlanPermissions(planId: number, permissionIds: number[]) {
    const response = await apiClient.put(`/admin/plan-permissions/plan/${planId}/assign`, {
      permissionIds,
    });
    return response.data;
  }

  async getTenantPlanPermissions() {
    const response = await apiClient.get('/admin/plan-permissions/tenant/current');
    return response.data;
  }

  // Plan Module APIs
  async getPlansWithModules() {
    const response = await apiClient.get('/admin/plan-modules/plans');
    return response.data;
  }

  async getPlanModules(planId: number) {
    const response = await apiClient.get(`/admin/plan-modules/plan/${planId}`);
    return response.data;
  }

  async getAvailableModules() {
    const response = await apiClient.get('/admin/plan-modules/modules');
    return response.data;
  }

  async assignPlanModules(planId: number, modules: string[]) {
    const response = await apiClient.put(`/admin/plan-modules/plan/${planId}/assign`, {
      modules,
    });
    return response.data;
  }

  async getTenantPlanModules() {
    const response = await apiClient.get('/admin/plan-modules/tenant/current');
    return response.data;
  }

  // Plan Route APIs
  async syncAvailableRoutes(routes: any[]) {
    const response = await apiClient.post('/admin/plan-routes/routes/sync', {
      routes,
    });
    return response.data;
  }

  async getPlansWithRoutes() {
    const response = await apiClient.get('/admin/plan-routes/plans');
    return response.data;
  }

  async getPlanRoutes(planId: number) {
    const response = await apiClient.get(`/admin/plan-routes/plan/${planId}`);
    return response.data;
  }

  async assignPlanRoutes(planId: number, routes: string[], routePaths: Record<string, string>) {
    const response = await apiClient.put(`/admin/plan-routes/plan/${planId}/assign`, {
      routes,
      routePaths,
    });
    return response.data;
  }

  async getTenantPlanRoutes() {
    const response = await apiClient.get('/admin/plan-routes/tenant/current');
    return response.data;
  }

  // Payslip APIs
  async getPayslips() {
    const response = await apiClient.get('/admin/payslips');
    return response.data;
  }

  async getPayslipById(id: number) {
    const response = await apiClient.get(`/admin/payslips/${id}`);
    return response.data;
  }

  async getPayslipsByEmployee(employeeId: number) {
    const response = await apiClient.get(`/admin/payslips/employee/${employeeId}`);
    return response.data;
  }

  async getPayslipWithDetails(id: number) {
    const response = await apiClient.get(`/admin/payslips/${id}/details`);
    return response.data;
  }

  async generatePayslip(data: any) {
    const response = await apiClient.post('/admin/payslips/generate', data);
    return response.data;
  }

  async generatePayslipWithPDF(data: any) {
    const response = await apiClient.post('/admin/payslips/generate-with-pdf', data);
    return response.data;
  }

  async sendPayslipEmail(payslipId: number, email: string) {
    const response = await apiClient.post(`/admin/payslips/${payslipId}/send-email`, { email });
    return response.data;
  }

  async getPayslipStats() {
    const response = await apiClient.get('/admin/payslips/stats');
    return response.data;
  }

  // Payroll Tools: Bank export, Form 16, LOP sync
  async getBankExportPreview(periodStart: string, periodEnd: string) {
    const params = new URLSearchParams({ period_start: periodStart, period_end: periodEnd });
    const response = await apiClient.get(`/admin/payroll-tools/bank-export/preview?${params}`);
    return response.data;
  }

  async downloadBankExport(periodStart: string, periodEnd: string, format: 'generic' | 'icici' | 'yesbank' | 'hsbc' = 'generic') {
    const params = new URLSearchParams({ period_start: periodStart, period_end: periodEnd, format });
    const response = await apiClient.get(`/admin/payroll-tools/bank-export?${params}`, {
      responseType: 'text',
    });
    const csv = typeof response.data === 'string' ? response.data : '';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bank_export_${periodStart}_${periodEnd}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    return { success: true };
  }

  async getForm16List(params?: { employee_id?: number; financial_year?: string }) {
    const q = new URLSearchParams();
    if (params?.employee_id) q.append('employee_id', String(params.employee_id));
    if (params?.financial_year) q.append('financial_year', params.financial_year);
    const response = await apiClient.get(`/admin/payroll-tools/form16${q.toString() ? `?${q}` : ''}`);
    return response.data;
  }

  async generateForm16(data: { employee_id: number; financial_year: string; company_name?: string; signature_path?: string }) {
    const response = await apiClient.post('/admin/payroll-tools/form16/generate', data);
    return response.data;
  }

  async signForm16(form16Id: number, data: { signed_by_employee_id?: number; signature_path?: string }) {
    const response = await apiClient.post(`/admin/payroll-tools/form16/${form16Id}/sign`, data);
    return response.data;
  }

  async getLopSyncPreview(periodStart: string, periodEnd: string, employeeId?: number) {
    const params = new URLSearchParams({ period_start: periodStart, period_end: periodEnd });
    if (employeeId != null) params.append('employee_id', String(employeeId));
    const response = await apiClient.get(`/admin/payroll-tools/lop-sync?${params}`);
    return response.data;
  }

  async applyLopToPayroll(periodStart: string, periodEnd: string) {
    const response = await apiClient.post('/admin/payroll-tools/lop-sync/apply', {
      period_start: periodStart,
      period_end: periodEnd,
    });
    return response.data;
  }
  async getEmployeeTimeline(employeeId: number) {
    const response = await apiClient.get(`/admin/employee/${employeeId}/timeline`);
    return response.data;
  }

  // Company Policies (Admin)
  async getCompanyPolicies() {
    const response = await apiClient.get('/admin/company-policies');
    return response.data;
  }
  
  async createCompanyPolicy(data: any) {
    const response = await apiClient.post('/admin/company-policies', data);
    return response.data;
  }

  async updateCompanyPolicy(id: number, data: any) {
    const response = await apiClient.put(`/admin/company-policies/${id}`, data);
    return response.data;
  }

  async getPolicyAcknowledgments(policyId: number) {
    const response = await apiClient.get(`/admin/company-policies/${policyId}/acknowledgments`);
    return response.data;
  }

  async assignCompanyPolicy(policyId: number, data: any) {
    const response = await apiClient.post(`/admin/company-policies/${policyId}/assign`, data);
    return response.data;
  }

  async remindCompanyPolicy(policyId: number, data: any = {}) {
    const response = await apiClient.post(`/admin/company-policies/${policyId}/remind`, data);
    return response.data;
  }

  async deleteCompanyPolicy(id: number) {
    const response = await apiClient.delete(`/admin/company-policies/${id}`);
    return response.data;
  }

  // Company Policies (Employee)
  async getEmployeePolicies() {
    const response = await apiClient.get('/employee/policies/acknowledgments');
    return response.data;
  }

  async acceptEmployeePolicy(id: number, data: any) {
    const payload = typeof data === 'string' ? { signature_text: data, signature_type: 'typed' } : data;
    const response = await apiClient.post(`/employee/policies/acknowledgments/${id}/accept`, payload);
    return response.data;
  }

  async downloadPolicyPDF(ackId: number) {
    const response = await apiClient.get(`/employee/policies/acknowledgments/${ackId}/download-pdf`, {
      responseType: 'blob'
    });
    return response;
  }

  // System Configuration APIs
  async getRoutePermissionsMapping() {
    const response = await apiClient.get('/system/route-permissions');
    return response.data;
  }

  async updateRoutePermission(routeKey: string, requiredPermissions: string[]) {
    const response = await apiClient.put(`/system/route-permissions/${routeKey}`, {
      required_permissions: requiredPermissions
    });
    return response.data;
  }

  // Admin: Employee Documents (Offer Letters & Experience Letters)
  async generateEmployeeDocument(payload: any) {
    const response = await apiClient.post('/admin/employee-documents/generate', payload);
    return response.data;
  }

  async releaseEmployeeDocument(id: number) {
    const response = await apiClient.post(`/admin/employee-documents/${id}/release`);
    return response.data;
  }

  async getAdminEmployeeDocuments(params?: any) {
    const response = await apiClient.get('/admin/employee-documents', { params });
    return response.data;
  }

  async getAdminEmployeeDocumentById(id: number) {
    const response = await apiClient.get(`/admin/employee-documents/${id}`);
    return response.data;
  }

  async deleteAdminEmployeeDocument(id: number) {
    const response = await apiClient.delete(`/admin/employee-documents/${id}`);
    return response.data;
  }

  // Employee Portal: My Documents (Offer Letters & Experience Letters)
  async getEmployeeDocuments() {
    const response = await apiClient.get('/employee/documents');
    return response.data;
  }

  async acceptEmployeeDocument(id: number, payload: { signature_type: string; signature_text?: string; signature_image?: string }) {
    const response = await apiClient.post(`/employee/documents/${id}/accept`, payload);
    return response.data;
  }

  async rejectEmployeeDocument(id: number) {
    const response = await apiClient.post(`/employee/documents/${id}/reject`);
    return response.data;
  }

  async downloadEmployeeDocumentPDF(filename: string) {
    const response = await apiClient.get(`/admin/employee-documents/download/${filename}`, {
      responseType: 'blob',
    });
    return response;
  }

}

export default new ApiService();

